import React from 'react';
import { ToolCardChip } from '../../../types/chat';
import { ToolCardMeta } from './toolCardMeta';

export type ToolCardStatus = 'running' | 'awaiting' | 'executed' | 'expired' | 'generating' | 'completed' | 'error' | 'unknown';

const STATUS_PILL: Record<ToolCardStatus, { label: string; className: string }> = {
  running: { label: '実行中', className: 'bg-blue-50 text-blue-600' },
  awaiting: { label: '確認待ち', className: 'bg-amber-50 text-amber-700' },
  executed: { label: '実行済み', className: 'bg-gray-100 text-gray-500' },
  expired: { label: '期限切れ', className: 'bg-gray-100 text-gray-500' },
  generating: { label: '生成中', className: 'bg-violet-50 text-violet-600' },
  completed: { label: '完了', className: 'bg-emerald-50 text-emerald-600' },
  error: { label: '失敗', className: 'bg-rose-50 text-rose-600' },
  // 状態取得を打ち切った（ジョブ自体は続いているかもしれない）
  unknown: { label: '状態不明', className: 'bg-gray-100 text-gray-500' },
};

interface ToolCardFrameProps {
  label: string;
  meta: ToolCardMeta;
  status: ToolCardStatus;
  chips?: ToolCardChip[];
  argsSummary?: string | null;
  children?: React.ReactNode;
}

const Spinner: React.FC = () => (
  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

/**
 * ツールカードの共通枠: 左アクセント線・種別アイコン・ラベル・状態ピル・設定チップ。
 */
const ToolCardFrame: React.FC<ToolCardFrameProps> = ({ label, meta, status, chips, argsSummary, children }) => {
  const Icon = meta.icon;
  const pill = STATUS_PILL[status];
  const busy = status === 'running' || status === 'generating';

  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className={`absolute inset-y-0 left-0 w-1 ${meta.accent}`} aria-hidden="true" />
      <div className="px-3 py-2.5 pl-4 sm:px-4 sm:pl-5">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.iconWrap}`}>
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{label}</span>
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${pill.className}`}>
            {busy && <Spinner />}
            {pill.label}
          </span>
        </div>

        {chips && chips.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {chips.map((chip, i) => (
              <span key={`${chip.label}-${i}`} className="inline-flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                <span className="text-gray-400">{chip.label}</span>
                <span className="truncate">{chip.value}</span>
              </span>
            ))}
          </div>
        ) : argsSummary ? (
          <div className="mt-1 truncate text-xs text-gray-500">{argsSummary}</div>
        ) : null}

        {children && <div className="mt-2">{children}</div>}
      </div>
    </div>
  );
};

export default ToolCardFrame;
